import { validatePlan } from './model';

export const IMAGE_LIMIT = 10 * 1024 * 1024;
export const JSON_LIMIT = 15 * 1024 * 1024;
const MIME = ['image/png', 'image/jpeg', 'image/webp'];
function signature(bytes, mime) {
  const at = (s, offset) => [...s].every((c, i) => bytes[offset + i] === c.charCodeAt(0));
  if (mime === 'image/png') return bytes[0] === 137 && at('PNG\r\n\x1a\n', 1);
  if (mime === 'image/jpeg') return bytes[0] === 255 && bytes[1] === 216 && bytes[2] === 255;
  return at('RIFF', 0) && at('WEBP', 8);
}
export async function validateImage(dataUrl) {
  if (typeof dataUrl !== 'string' || dataUrl.length > Math.ceil(IMAGE_LIMIT / 3) * 4 + 40) throw new Error('La imagen supera 10 MiB.');
  const match = /^data:(image\/(?:png|jpeg|webp));base64,([A-Za-z0-9+/]+={0,2})$/.exec(dataUrl);
  if (!match || match[2].length % 4 !== 0) throw new Error('Usá una imagen PNG, JPEG o WebP incorporada en el archivo.');
  let binary;
  try { binary = atob(match[2]); } catch { throw new Error('La imagen contiene datos inválidos.'); }
  if (binary.length > IMAGE_LIMIT || !signature(Uint8Array.from(binary.slice(0, 12), c => c.charCodeAt(0)), match[1])) throw new Error('El contenido de la imagen no coincide con su formato o excede 10 MiB.');
  return new Promise((resolve, reject) => {
    const image = new Image();
    const finish = () => { image.onload = null; image.onerror = null; image.src = ''; };
    image.onload = () => {
      const width = image.naturalWidth, height = image.naturalHeight;
      finish();
      if (width < 1 || height < 1 || width > 4096 || height > 4096) reject(new Error('La imagen debe tener entre 1 y 4096 píxeles por lado.'));
      else resolve({ dataUrl, width, height });
    };
    image.onerror = () => { finish(); reject(new Error('No se pudo decodificar la imagen.')); };
    image.src = dataUrl;
  });
}
export async function readBackground(file) {
  if (!file || !MIME.includes(file.type) || !/\.(png|jpe?g|webp)$/i.test(file.name) || file.size > IMAGE_LIMIT) throw new Error('Seleccioná PNG, JPEG o WebP, de hasta 10 MiB y 4096 × 4096 píxeles.');
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('No se pudo leer la imagen.'));
    reader.readAsDataURL(file);
  });
  return validateImage(dataUrl);
}
export async function importPlan(file) {
  if (!file || !/\.json$/i.test(file.name) || file.size > JSON_LIMIT || (file.type && !['application/json', 'text/json', 'text/plain'].includes(file.type))) throw new Error('Seleccioná un archivo .json de hasta 15 MiB.');
  let parsed;
  try { parsed = JSON.parse(await file.text()); } catch { throw new Error('El archivo no contiene JSON válido.'); }
  const plan = validatePlan(parsed);
  if (plan.background) {
    const image = await validateImage(plan.background.dataUrl);
    if (image.width !== plan.background.width || image.height !== plan.background.height) throw new Error('Las dimensiones declaradas no coinciden con la imagen.');
    plan.background = image;
  }
  return plan;
}
export function exportPlan(plan) {
  const clean = validatePlan(plan);
  // Data URL embebida: el archivo es independiente de la sesión y del servidor.
  const url = URL.createObjectURL(new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${clean.name.replace(/[^a-zA-Z0-9_-]/g, '_') || 'plano'}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
